import {
  Badge,
  Box,
  Button,
  Card,
  CloseButton,
  Dialog,
  HStack,
  Link,
  Portal,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FileSpreadsheet, LucideIcon, Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { track } from "@/helpers/posthog";
import { useMerchant } from "@/hooks/useMerchant";
import { useTRPC } from "../../../../trpc/client";
import { ONBOARDING_LINKS } from "../../../public/onboarding/constants";
import { InvoiceModal } from "./Buttons";

type Props = { onOpen: (modal: InvoiceModal) => void };

const OptionCard = ({
  icon: Icon,
  title,
  description,
  badge,
  disabled,
  hint,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  badge?: string;
  disabled?: boolean;
  hint?: string;
  onClick: () => void;
}) => (
  <Box
    as="button"
    textAlign="left"
    bg="white"
    borderWidth="1px"
    borderColor="palomma.100"
    rounded="md"
    p={3}
    opacity={disabled ? 0.5 : 1}
    cursor={disabled ? "not-allowed" : "pointer"}
    _hover={disabled ? {} : { borderColor: "palomma.300" }}
    onClick={disabled ? undefined : onClick}
  >
    <HStack gap={2} align="flex-start">
      <Box color="palomma.600" pt={0.5}>
        <Icon size={18} />
      </Box>
      <VStack align="stretch" gap={0.5} minW={0}>
        <HStack gap={2}>
          <Text fontWeight="semibold" fontSize="sm">
            {title}
          </Text>
          {badge && (
            <Badge colorPalette="purple" rounded="full" size="sm" px={1.5}>
              {badge}
            </Badge>
          )}
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {disabled && hint ? hint : description}
        </Text>
      </VStack>
    </HStack>
  </Box>
);

const InvoicesEmptyState = ({ onOpen }: Props): JSX.Element => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();
  const [erpOpen, setErpOpen] = useState(false);
  const { data: catalogs } = useQuery(trpc.onboarding.catalogs.queryOptions());

  if (!merchant) return <></>;

  const hasErp = merchant.erp !== "none";
  const erpName =
    catalogs?.erps.find((erp) => erp.value === merchant.erp)?.label ?? "tu ERP";

  const open = (option: "erp" | "excel" | "single") => {
    track("onboarding_empty_state_clicked", { option });
    if (option === "erp") setErpOpen(true);
    else onOpen(option === "excel" ? "upload" : "add");
  };

  return (
    <Card.Root
      bg="palomma.50"
      rounded="md"
      borderWidth="1px"
      borderColor="palomma.200"
      borderLeftWidth="4px"
      borderLeftColor="palomma.500"
      boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      px={3}
      py={3}
    >
      <Card.Body p={0} display="flex" flexDirection="column" gap={2}>
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="palomma.800">
            Aún no tienes recaudos. Este es tu siguiente paso.
          </Text>
          <Text fontSize="xs" color="gray.500">
            Elige cómo quieres cargar tu cartera. Los inquilinos se crean solos
            con cada recaudo.
          </Text>
        </Box>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={2}>
          <OptionCard
            icon={RefreshCw}
            title={
              hasErp ? `Sincronizar con ${erpName}` : "Sincronizar con mi ERP"
            }
            description="Tu cartera llega sola cada día, sin cargar archivos."
            disabled={!hasErp}
            hint="Tu ERP no tiene integración con Palomma."
            onClick={() => open("erp")}
          />
          <OptionCard
            icon={FileSpreadsheet}
            title="Varios a la vez con Excel"
            description="Descarga la plantilla, llénala y súbela. Revisas antes de cargar."
            badge="Recomendado"
            onClick={() => open("excel")}
          />
          <OptionCard
            icon={Plus}
            title="Uno solo, ahora mismo"
            description="Un formulario corto. Ideal para probar cómo funciona."
            onClick={() => open("single")}
          />
        </SimpleGrid>
      </Card.Body>

      <Dialog.Root
        open={erpOpen}
        onOpenChange={(e) => !e.open && setErpOpen(false)}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content borderRadius="md" maxWidth="480px">
              <Dialog.Header>
                <Dialog.Title fontSize="lg" fontWeight="semibold">
                  {merchant.erpConfigured
                    ? `${erpName} ya está conectado`
                    : `Sincroniza tu cartera con ${erpName}`}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
              <Dialog.Body>
                <Text fontSize="sm" color="gray.700">
                  {merchant.erpConfigured
                    ? "Los recaudos llegan automáticamente desde tu ERP. Si no los ves en unas horas, escríbenos."
                    : `Contacta a ${erpName} para que te ayude con la sincronización de tu cartera. Ellos activan la conexión con Palomma y desde ese momento los recaudos llegan solos.`}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={3}>
                  ¿Necesitas ayuda?{" "}
                  <Link
                    href={ONBOARDING_LINKS.whatsappSupport}
                    target="_blank"
                    rel="noreferrer"
                    color="palomma.700"
                    textDecoration="underline"
                    textUnderlineOffset="4px"
                  >
                    Escríbenos por WhatsApp
                  </Link>
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  colorPalette="palomma"
                  onClick={() => setErpOpen(false)}
                >
                  Entendido
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Card.Root>
  );
};

export default InvoicesEmptyState;
